import { get } from "svelte/store";
import { authToken, handleSignOut, handleTokenExpiration } from "../security/auth-handler.js";
import { getEnvironment } from "../../config/env.js";
import { navigateTo } from "../routing/route-action.js";
import { ROUTE } from "../../constants/route-constants.js";
import { logInfo, logWarning, logError } from "../logging/log-handler.js";
import { userStore } from "../stores/user-store.js";

interface RequestOptions extends RequestInit {
  params?: Record<string, string>;
}

// Public endpoints that don't require authentication
const PUBLIC_ENDPOINTS = ["/getADUserByUsername", "/health", "/"];

export async function apiRequest(endpoint: string, options: RequestOptions = {}) {
  // Get token from both store and sessionStorage for redundancy
  const storeToken = get(authToken);
  const sessionToken = sessionStorage.getItem("authToken");
  const token = storeToken || sessionToken;
  const userId = get(userStore).user?.email;
  const sessionId = localStorage.getItem("sessionId");

  // Check if this is a public endpoint
  const isPublicEndpoint = PUBLIC_ENDPOINTS.some((publicEndpoint) => endpoint.startsWith(publicEndpoint));

  if (!token && !isPublicEndpoint) {
    logError(new Error("No authorization token available"), "api-request", undefined, { userId: userId, operationName: endpoint, sessionId: sessionId }, { hasStoreToken: !!storeToken, hasSessionToken: !!sessionToken, endpoint: endpoint });
    await handleTokenExpiration();
    throw new Error("No authorization token available");
  }

  const baseUrl = getEnvironment().serverUrl;

  // Add query parameters if they exist
  let url = `${baseUrl}${endpoint}`;
  if (options.params) {
    const queryParams = new URLSearchParams(options.params).toString();
    url += `?${queryParams}`;
  }

  // Merge default headers with provided headers
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    Accept: "application/json",
    // Include sessionId in headers if available
    ...(sessionId ? { "x-session-id": sessionId } : {}),
    // Only set Content-Type for JSON bodies, let browser set it for FormData
    ...(options.body && !(options.body instanceof FormData) ? { "Content-Type": "application/json" } : {}),
    ...options.headers,
  };

  // Only stringify body if it's not FormData
  const body = options.body instanceof FormData ? options.body : options.body ? JSON.stringify(options.body) : undefined;

  try {
    const response = await fetch(url, {
      ...options,
      body,
      headers,
      credentials: "include",
      mode: "cors",
    });

    if (!response.ok) {
      let errorMessage;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || response.statusText;
        logError(new Error(`HTTP ${response.status}: ${errorMessage}`), "api-request", { userId: userId, operationName: endpoint, sessionId: sessionId }, { status: response.status, errorData, url });

        // Handle token expiration
        if (response.status === 401 || errorMessage.includes("Token expired")) {
          logInfo("Token expired, handling expiration", "api-request", { userId: userId, operationName: endpoint, sessionId: sessionId }, {});
          await handleTokenExpiration();
          throw new Error("Token expired");
        }
      } catch (parseError) {
        errorMessage = (await response.text()) || response.statusText;
      }
      throw new Error(errorMessage);
    }

    // Only try to parse JSON if there's content
    const contentType = response.headers.get("content-type");

    if (contentType && contentType.includes("application/json")) {
      const jsonData = await response.json();
      return jsonData;
    }

    // Handle streaming responses
    if (contentType && contentType.includes("text/event-stream")) {
      return response;
    }
    const textData = await response.text();
    return textData;
  } catch (error: unknown) {
    logError(error instanceof Error ? error : new Error("Unknown fetch error"), "api-request", {}, {});
    if (error instanceof Error) {
      if (error.name === "TokenExpiredError" || error.message.includes("Token expired")) {
        logInfo("Token expired, handling expiration", "api-request", {}, {});
        await handleTokenExpiration();
        throw error;
      }
    }

    throw error;
  }
}

// Helper methods for common HTTP methods
export const api = {
  get: (endpoint: string, params?: Record<string, string>) => apiRequest(endpoint, { method: "GET", params }),

  post: (endpoint: string, data?: any, params?: Record<string, string>) =>
    apiRequest(endpoint, {
      method: "POST",
      body: data,
      params,
    }),

  put: (endpoint: string, data?: any, params?: Record<string, string>) =>
    apiRequest(endpoint, {
      method: "PUT",
      body: data,
      params,
    }),

  delete: (endpoint: string, params?: Record<string, string>) => apiRequest(endpoint, { method: "DELETE", params }),
};
